import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Users, AlertTriangle, TrendingUp, CheckCircle2, Clock } from 'lucide-react';

/**
 * KpiDashboard component for Resource Planning page
 * Displays summary cards and hours breakdown
 */
const KpiDashboard = ({
  // Data
  utilizationData,
  filteredSummaryKPI,
  filteredHoursKPI,
  planningSettings,
  // Filter state (for showing "filtered" indicator)
  filterEmployee,
  filterProject
}) => {
  // Calculate derived values for hours display
  const totalGrossHours = Math.round((filteredHoursKPI.totalAvailableProjectHours / (planningSettings.project_work_percent / 100) + filteredHoursKPI.absenceHours) * 10) / 10;
  const totalNetHours = Math.round((totalGrossHours - filteredHoursKPI.absenceHours) * 10) / 10;
  const adminTotal = Math.round(totalNetHours * ((100 - planningSettings.project_work_percent) / 100) * 10) / 10;
  const adminNotBooked = Math.round((adminTotal - filteredHoursKPI.totalAdminHours) * 10) / 10;
  const freeProjectHours = Math.max(0, Math.round((filteredHoursKPI.totalAvailableProjectHours - filteredHoursKPI.totalProjectHours) * 10) / 10);

  return (
    <>
      {/* Summary Cards - Filtered metrics */}
      {utilizationData && (
        <div className="grid grid-cols-4 gap-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Card className="cursor-help">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Mitarbeiter</p>
                        <p className="text-2xl font-bold">{filteredSummaryKPI.total}</p>
                        {(filterEmployee || filterProject) && (
                          <p className="text-xs text-blue-600">gefiltert</p>
                        )}
                      </div>
                      <Users className="w-8 h-8 text-muted-foreground/30" />
                    </div>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent>
                <p>Anzahl Mitarbeiter im gewählten Filter</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Card className={`cursor-help ${filteredSummaryKPI.overbooked > 0 ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20' : ''}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Überbucht</p>
                        <p className="text-2xl font-bold text-red-600">{filteredSummaryKPI.overbooked}</p>
                      </div>
                      <AlertTriangle className="w-8 h-8 text-red-200" />
                    </div>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">Auslastung &gt; 100%</p>
                <p className="text-xs text-muted-foreground">Mehr Stunden geplant als verfügbar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Card className={`cursor-help ${filteredSummaryKPI.high > 0 ? 'border-amber-200 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/20' : ''}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Hohe Auslastung</p>
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{filteredSummaryKPI.high}</p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-amber-200 dark:text-amber-600" />
                    </div>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">Auslastung 80–100%</p>
                <p className="text-xs text-muted-foreground">Gut ausgelastet, wenig Puffer</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Card className="cursor-help">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Verfügbar</p>
                        <p className="text-2xl font-bold text-green-600">
                          {filteredSummaryKPI.normal + filteredSummaryKPI.low}
                        </p>
                      </div>
                      <CheckCircle2 className="w-8 h-8 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">Auslastung &lt; 80%</p>
                <p className="text-xs text-muted-foreground">Kapazität für weitere Aufgaben</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* Hours KPI Cards - Visual calculation flow */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Stunden im gewählten Zeitraum
            <Badge variant="outline" className="ml-2 text-xs font-normal">
              {filteredHoursKPI.workingDays} Arbeitstage • {filteredHoursKPI.employeeCount} MA
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <TooltipProvider delayDuration={200}>
            <div className="flex gap-4 items-stretch">
              {/* Left side: Total calculation */}
              <div className="flex flex-col justify-between min-w-[140px]">
                {/* Top: Soll-Stunden */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-center cursor-help hover:ring-2 hover:ring-slate-300 dark:hover:ring-slate-600">
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Soll-Stunden</p>
                      <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{totalGrossHours}h</p>
                      <p className="text-[9px] text-slate-400 dark:text-slate-500">
                        {filteredHoursKPI.workingDays} Tage × {filteredHoursKPI.employeeCount} MA
                      </p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="bg-slate-900 text-white p-3">
                    <div className="text-xs">
                      <p className="font-bold">Soll-Stunden Berechnung:</p>
                      <p className="text-slate-300 mt-1">Arbeitstage × Tagesstunden × MA</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
                
                {/* Middle: Abwesenheiten */}
                <div className="flex flex-col items-center flex-1 justify-center">
                  <div className="h-full min-h-[20px] w-0.5 bg-orange-300 dark:bg-orange-600"></div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 border-dashed rounded-lg px-3 py-1.5 text-center cursor-help hover:ring-2 hover:ring-orange-300 dark:hover:ring-orange-600 my-1">
                        <p className="text-[9px] text-orange-500 dark:text-orange-400">Abwesenheiten</p>
                        <p className="text-sm font-bold text-orange-600 dark:text-orange-400">−{filteredHoursKPI.absenceHours}h</p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="bg-slate-900 text-white p-3">
                      <div className="text-xs">
                        <p className="font-bold text-orange-300">Abwesenheiten:</p>
                        <p className="text-slate-300 mt-1">Urlaub, Krankheit, etc.</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  <div className="h-full min-h-[20px] w-0.5 bg-blue-300 dark:bg-blue-600"></div>
                </div>
                
                {/* Bottom: Netto verfügbar */}
                <div className="bg-blue-100 dark:bg-blue-900/40 border-2 border-blue-300 dark:border-blue-600 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-blue-500 dark:text-blue-400">Netto verfügbar</p>
                  <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{totalNetHours}h</p>
                </div>
              </div>
              
              {/* Arrow to split */}
              <div className="flex items-center justify-center">
                <span className="text-slate-300 dark:text-slate-600 text-xl">→</span>
              </div>
              
              {/* Right side: Split into categories */}
              <div className="flex flex-col gap-2 flex-1">
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium text-center">Aufteilung</p>
                
                {/* Column headers for Geplant vs Frei */}
                <div className="grid grid-cols-2 gap-2 mb-1">
                  <div className="text-center">
                    <span className="text-xs font-bold text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/40 px-3 py-1 rounded-full">✓ Geplant</span>
                  </div>
                  <div className="text-center">
                    <span className="text-xs font-bold text-purple-700 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/40 px-3 py-1 rounded-full">○ Verfügbar</span>
                  </div>
                </div>
                
                {/* Project section */}
                <div className="rounded-lg border border-blue-200 dark:border-blue-700 overflow-hidden">
                  <div className="bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 flex items-center justify-between border-b border-blue-200 dark:border-blue-700">
                    <span className="text-xs font-bold text-blue-700 dark:text-blue-300">Projekt ({planningSettings.project_work_percent}%)</span>
                    <span className="text-sm font-bold text-blue-700 dark:text-blue-300">{filteredHoursKPI.totalAvailableProjectHours}h</span>
                  </div>
                  <div className="grid grid-cols-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="bg-green-100 dark:bg-green-900/40 border-r border-green-200 dark:border-green-800 p-3 text-center cursor-help hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors">
                          <p className="text-[10px] text-green-600 dark:text-green-400 font-medium">Geplant</p>
                          <p className="text-xl font-bold text-green-700 dark:text-green-300">{filteredHoursKPI.totalProjectHours}h</p>
                          {filteredHoursKPI.overbookedHours > 0 && (
                            <p className="text-[9px] text-red-500 font-medium">(davon {filteredHoursKPI.overbookedHours}h überbucht)</p>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="bg-slate-900 text-white p-3 max-w-xs">
                        <div className="text-xs space-y-1">
                          <p className="font-bold text-green-300">Geplante Projektstunden:</p>
                          <p className="text-slate-300">Summe aller Task-Stunden: {filteredHoursKPI.totalProjectHours}h</p>
                          {filteredHoursKPI.overbookedHours > 0 && (
                            <>
                              <p className="text-red-400 mt-1">⚠ Überbuchung: {filteredHoursKPI.overbookedHours}h</p>
                              <p className="text-slate-400 text-[10px]">Tage mit &gt;100% Allokation</p>
                            </>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="bg-purple-100 dark:bg-purple-900/30 p-3 text-center cursor-help hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors">
                          <p className="text-[10px] text-purple-600 dark:text-purple-400 font-medium">Frei</p>
                          <p className="text-xl font-bold text-purple-700 dark:text-purple-300">{freeProjectHours}h</p>
                          {filteredHoursKPI.overbookedHours > 0 && (
                            <p className="text-[9px] text-red-500 font-medium">(−{filteredHoursKPI.overbookedHours}h Überb.)</p>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="bg-slate-900 text-white p-3 max-w-xs">
                        <div className="text-xs space-y-1">
                          <p className="font-bold text-purple-300">Freie Projekt-Kapazität:</p>
                          <p className="text-slate-300">{filteredHoursKPI.totalAvailableProjectHours}h − {filteredHoursKPI.totalProjectHours}h = {freeProjectHours}h</p>
                          {filteredHoursKPI.overbookedHours > 0 && (
                            <>
                              <hr className="border-slate-600 my-1" />
                              <p className="text-red-400">⚠ Hinweis: Die {filteredHoursKPI.overbookedHours}h Überbuchung reduzieren die freie Kapazität!</p>
                              <p className="text-slate-400 text-[10px]">Ohne Überbuchung wären {Math.round((freeProjectHours + filteredHoursKPI.overbookedHours) * 10) / 10}h frei</p>
                            </>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  {/* Progress bar with overbooking indicator */}
                  <div className="h-2 bg-purple-200 dark:bg-purple-900/50 relative">
                    <div 
                      className={`h-full transition-all ${filteredHoursKPI.utilizationPercent > 100 ? 'bg-red-500' : filteredHoursKPI.utilizationPercent > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.min(filteredHoursKPI.utilizationPercent, 100)}%` }}
                    ></div>
                    {filteredHoursKPI.overbookedHours > 0 && (
                      <div 
                        className="absolute top-0 h-full bg-red-600 opacity-50"
                        style={{ 
                          left: '100%',
                          width: `${Math.min((filteredHoursKPI.overbookedHours / filteredHoursKPI.totalAvailableProjectHours) * 100, 20)}%`
                        }}
                      ></div>
                    )}
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800 px-3 py-1 flex justify-between items-center">
                    <span className={`text-[10px] font-medium ${filteredHoursKPI.utilizationPercent > 100 ? 'text-red-600 dark:text-red-400' : filteredHoursKPI.utilizationPercent > 80 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                      {filteredHoursKPI.utilizationPercent}% Planungsgrad
                    </span>
                    {filteredHoursKPI.overbookedHours > 0 && (
                      <span className="text-[10px] font-medium text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40 px-2 py-0.5 rounded">
                        ⚠ {filteredHoursKPI.overbookedHours}h überbucht
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Admin section */}
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="bg-slate-100 dark:bg-slate-800 px-3 py-1.5 flex items-center justify-between border-b border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Admin ({100 - planningSettings.project_work_percent}%)</span>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{adminTotal}h</span>
                  </div>
                  <div className="grid grid-cols-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="bg-green-50 dark:bg-green-900/30 border-r border-green-200 dark:border-green-800 p-3 text-center cursor-help hover:bg-green-100 dark:hover:bg-green-800/40 transition-colors">
                          <p className="text-[10px] text-green-600 dark:text-green-400 font-medium">Bei Tasks</p>
                          <p className="text-xl font-bold text-green-700 dark:text-green-300">{filteredHoursKPI.totalAdminHours}h</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="bg-slate-900 text-white p-3">
                        <div className="text-xs space-y-1">
                          <p className="font-bold text-green-300">Admin bei Tasks:</p>
                          <p className="text-slate-400">An Tagen mit Tasks, proportional zur Allokation</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 text-center cursor-help hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors">
                          <p className="text-[10px] text-purple-600 dark:text-purple-400 font-medium">Ohne Tasks</p>
                          <p className="text-xl font-bold text-purple-700 dark:text-purple-300">{adminNotBooked}h</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="bg-slate-900 text-white p-3">
                        <div className="text-xs space-y-1">
                          <p className="font-bold text-purple-300">Admin ohne Tasks:</p>
                          <p className="text-slate-400">{adminTotal}h − {filteredHoursKPI.totalAdminHours}h = {adminNotBooked}h</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                
                {/* Summary totals */}
                <div className="grid grid-cols-2 gap-2 mt-1 pt-2 border-t border-dashed border-slate-300 dark:border-slate-600">
                  <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-2 text-center border border-green-200 dark:border-green-800">
                    <p className="text-[10px] text-green-600 dark:text-green-400">Σ Geplant</p>
                    <p className="text-lg font-bold text-green-700 dark:text-green-300">{Math.round((filteredHoursKPI.totalProjectHours + filteredHoursKPI.totalAdminHours) * 10) / 10}h</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center border border-purple-200 dark:border-purple-800">
                    <p className="text-[10px] text-purple-600 dark:text-purple-400">Σ Verfügbar</p>
                    <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{Math.round((freeProjectHours + adminNotBooked) * 10) / 10}h</p>
                  </div>
                </div>
                
                {/* Verification sum */}
                <div className="text-[10px] text-slate-400 text-center">
                  = {Math.round((filteredHoursKPI.totalProjectHours + filteredHoursKPI.totalAdminHours + freeProjectHours + adminNotBooked) * 10) / 10}h Netto ✓
                </div>
              </div>
            </div>
          </TooltipProvider>
        </CardContent>
      </Card>
    </>
  );
};

export default KpiDashboard;
